import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  AIAgent, 
  AgentPersonality, 
  AgentMetrics,
  AgentFeedback,
  AgentType
} from '@/types';

export class AgentConfigurationService {
  private readonly AGENTS_COLLECTION = 'aiAgents';
  private readonly PERSONALITIES_COLLECTION = 'agentPersonalities';
  private readonly METRICS_COLLECTION = 'agentMetrics';
  private readonly FEEDBACK_COLLECTION = 'agentFeedback';

  /**
   * Save or update an agent configuration
   */
  async saveAgentConfig(agent: AIAgent): Promise<void> {
    try {
      const agentRef = doc(db, this.AGENTS_COLLECTION, agent.id);
      await setDoc(agentRef, {
        ...agent,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving agent config:', error);
      throw new Error('Failed to save agent configuration');
    }
  }

  /**
   * Get agent configuration by ID
   */
  async getAgentConfig(agentId: string): Promise<AIAgent | null> {
    try {
      const agentRef = doc(db, this.AGENTS_COLLECTION, agentId);
      const agentSnap = await getDoc(agentRef);
      
      if (agentSnap.exists()) {
        return agentSnap.data() as AIAgent;
      }
      return null;
    } catch (error) {
      console.error('Error fetching agent config:', error);
      return null;
    }
  }

  /**
   * Get all active agents
   */
  async getActiveAgents(): Promise<AIAgent[]> {
    try {
      const agentsQuery = query(
        collection(db, this.AGENTS_COLLECTION),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(agentsQuery);
      
      const agents: AIAgent[] = [];
      querySnapshot.forEach((doc) => {
        agents.push({ id: doc.id, ...doc.data() } as AIAgent);
      });
      
      return agents;
    } catch (error) {
      console.error('Error fetching active agents:', error);
      return [];
    }
  }

  /**
   * Save client-specific personality for an agent
   */
  async saveClientPersonality(
    agentId: string, 
    clientId: string, 
    personality: AgentPersonality
  ): Promise<void> {
    try {
      const personalityId = `${agentId}_${clientId}`;
      const personalityRef = doc(db, this.PERSONALITIES_COLLECTION, personalityId);
      
      await setDoc(personalityRef, {
        id: personalityId,
        agentId,
        clientId,
        ...personality,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving agent personality:', error);
      throw new Error('Failed to save agent personality');
    }
  }

  /**
   * Get client-specific personality for an agent
   */
  async getClientPersonality(
    agentId: string, 
    clientId: string
  ): Promise<AgentPersonality | null> {
    try {
      const personalityId = `${agentId}_${clientId}`;
      const personalityRef = doc(db, this.PERSONALITIES_COLLECTION, personalityId);
      const personalitySnap = await getDoc(personalityRef);
      
      if (personalitySnap.exists()) {
        const data = personalitySnap.data();
        return {
          toneAdjustments: data.toneAdjustments,
          stylePreferences: data.stylePreferences,
          knowledgeBase: data.knowledgeBase || []
        } as AgentPersonality;
      }
      return null;
    } catch (error) {
      console.error('Error fetching agent personality:', error);
      return null;
    }
  }

  /**
   * Track agent performance
   */
  async trackAgentPerformance(
    agentId: string,
    clientId: string,
    feedback: AgentFeedback,
    tokensUsed: number,
    responseTime: number
  ): Promise<void> {
    try {
      console.log('🔄 trackAgentPerformance called with:', {
        agentId,
        clientId,
        feedback,
        tokensUsed,
        responseTime
      });

      // Save the feedback record - clean undefined values for Firestore
      const feedbackRef = doc(collection(db, this.FEEDBACK_COLLECTION));
      const cleanFeedback: any = {
        agentId: feedback.agentId,
        responseId: feedback.responseId,
        feedback: feedback.feedback,
        userId: feedback.userId,
        clientId: feedback.clientId,
        timestamp: serverTimestamp()
      };
      
      // Only add modifiedContent if it exists and is not undefined
      if (feedback.modifiedContent !== undefined && feedback.modifiedContent !== null) {
        cleanFeedback.modifiedContent = feedback.modifiedContent;
      }
      
      console.log('💾 Saving feedback record:', cleanFeedback);
      await setDoc(feedbackRef, cleanFeedback);
      console.log('✅ Feedback record saved successfully');

      // Update or create metrics
      console.log('📊 Updating agent metrics...');
      await this.updateAgentMetrics(agentId, clientId, feedback, tokensUsed, responseTime);
      console.log('✅ Agent metrics updated successfully');

      // Consolidate modified notes into small, stable rules (keep tiny footprint)
      if (feedback.feedback === 'modified' && feedback.modifiedContent) {
        try {
          const rule = this.normalizeRuleFromNote(feedback.modifiedContent);
          if (rule) {
            await this.addRuleToPersonality(agentId, clientId, rule);
          }
        } catch (e) {
          console.warn('Rule consolidation skipped:', e);
        }
      }
    } catch (error) {
      console.error('❌ Error tracking agent performance:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        agentId,
        clientId,
        feedback
      });
      // Don't throw - performance tracking shouldn't break the flow
    }
  }

  /**
   * Normalize a short user note/diff into a compact, reusable rule.
   */
  private normalizeRuleFromNote(noteRaw: string): string | null {
    const note = (noteRaw || '').toLowerCase();
    if (!note.trim()) return null;

    // Heuristic mappings for common edits
    if (/ap\s*style/.test(note) && /date/.test(note)) return 'Use AP style dates';
    if (/headline|title/.test(note) && /(short|concise|shorten)/.test(note)) return 'Shorten headlines';
    if (/superlative/.test(note) || /avoid\s+(very|really|extremely)/.test(note)) return 'Avoid superlatives and intensifiers';
    if (/exclamation|!/.test(note) && /(reduce|remove|less)/.test(note)) return 'Reduce emphatic punctuation';
    if (/(normalize|sentence case|title case|casing)/.test(note)) return 'Normalize casing for readability';
    if (/(shorten|more concise)/.test(note)) return 'Prefer concise wording';
    if (/(expand|add details|more detail)/.test(note)) return 'Include key supporting details';

    // Fallback: capitalize first letter, keep short
    const trimmed = noteRaw.trim();
    const compact = trimmed.length > 120 ? trimmed.slice(0, 120) + '…' : trimmed;
    return compact.charAt(0).toUpperCase() + compact.slice(1);
  }

  /**
   * Add a compact rule to client-specific personality (keep max 5 unique rules).
   */
  private async addRuleToPersonality(agentId: string, clientId: string, rule: string): Promise<void> {
    // Load existing personality (tolerate flexible schema)
    const existing = await this.getClientPersonality(agentId, clientId);
    const stylePreferences: string[] = Array.isArray((existing as any)?.stylePreferences)
      ? ([...(existing as any).stylePreferences] as string[])
      : [];

    // De-duplicate by case-insensitive match
    const exists = stylePreferences.some((r) => r.toLowerCase() === rule.toLowerCase());
    const next = exists ? stylePreferences : [rule, ...stylePreferences];
    // Keep top 5 only
    const limited = next.slice(0, 5);

    const personality: any = {
      toneAdjustments: (existing as any)?.toneAdjustments || {},
      stylePreferences: limited,
      knowledgeBase: (existing as any)?.knowledgeBase || [],
    };

    await this.saveClientPersonality(agentId, clientId, personality as AgentPersonality);
  }

  /**
   * Update agent metrics based on new feedback
   */
  private async updateAgentMetrics(
    agentId: string,
    clientId: string,
    feedback: AgentFeedback,
    tokensUsed: number,
    responseTime: number
  ): Promise<void> {
    const metricsId = `${agentId}_${clientId}`;
    const metricsRef = doc(db, this.METRICS_COLLECTION, metricsId);
    
    try {
      console.log('📈 updateAgentMetrics called:', {
        agentId,
        clientId,
        metricsId,
        feedback: feedback.feedback,
        tokensUsed,
        responseTime
      });

      const metricsSnap = await getDoc(metricsRef);
      console.log('📋 Existing metrics found:', metricsSnap.exists());
      
      if (metricsSnap.exists()) {
        // Update existing metrics
        const currentMetrics = metricsSnap.data() as AgentMetrics;
        console.log('📊 Current metrics:', currentMetrics);
        
        const totalInteractions = currentMetrics.totalInteractions + 1;

        const updates: Partial<AgentMetrics> = {
          // Self-heal legacy docs that predate owner scoping
          ...(feedback.userId ? { userId: feedback.userId } : {}),
          totalInteractions,
          averageTokensUsed: 
            (currentMetrics.averageTokensUsed * currentMetrics.totalInteractions + tokensUsed) / totalInteractions,
          averageResponseTime: 
            (currentMetrics.averageResponseTime * currentMetrics.totalInteractions + responseTime) / totalInteractions,
          lastCalculated: new Date().toISOString()
        };
        
        // Update feedback counts
        if (feedback.feedback === 'positive') {
          updates.positiveInteractions = currentMetrics.positiveInteractions + 1;
        } else if (feedback.feedback === 'negative') {
          updates.negativeInteractions = currentMetrics.negativeInteractions + 1;
        } else if (feedback.feedback === 'modified') {
          updates.modifiedInteractions = currentMetrics.modifiedInteractions + 1;
        }
        
        // Calculate autonomy level
        updates.autonomyLevel = this.calculateAutonomyLevel(
          updates.positiveInteractions || currentMetrics.positiveInteractions,
          updates.negativeInteractions || currentMetrics.negativeInteractions,
          updates.modifiedInteractions || currentMetrics.modifiedInteractions,
          totalInteractions
        );
        
        console.log('🔄 Updating existing metrics with:', updates);
        await updateDoc(metricsRef, updates);
        console.log('✅ Existing metrics updated successfully');
      } else {
        // Create new metrics
        const positiveCount = feedback.feedback === 'positive' ? 1 : 0;
        const negativeCount = feedback.feedback === 'negative' ? 1 : 0;
        const modifiedCount = feedback.feedback === 'modified' ? 1 : 0;
        
        const newMetrics: AgentMetrics = {
          agentId,
          clientId,
          ...(feedback.userId ? { userId: feedback.userId } : {}),
          totalInteractions: 1,
          positiveInteractions: positiveCount,
          negativeInteractions: negativeCount,
          modifiedInteractions: modifiedCount,
          averageTokensUsed: tokensUsed,
          averageResponseTime: responseTime,
          autonomyLevel: this.calculateAutonomyLevel(positiveCount, negativeCount, modifiedCount, 1),
          lastCalculated: new Date().toISOString()
        };
        
        console.log('🆕 Creating new metrics:', newMetrics);
        await setDoc(metricsRef, newMetrics);
        console.log('✅ New metrics created successfully');
      }
    } catch (error) {
      console.error('❌ Error updating agent metrics:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        metricsId,
        agentId,
        clientId
      });
    }
  }

  /**
   * Calculate autonomy level based on feedback
   */
  private calculateAutonomyLevel(
    positive: number,
    negative: number,
    modified: number,
    total: number
  ): number {
    // Allow autonomy to start building after just 1 interaction
    if (total < 1) return 0;
    
    const positiveRate = positive / total;
    const negativeRate = negative / total;
    const modifiedRate = modified / total;
    
    // Formula: high positive rate + low negative rate - modification penalty
    let autonomy = (positiveRate * 100) - (negativeRate * 50) - (modifiedRate * 25);
    
    // Apply a confidence multiplier based on total interactions
    // Early interactions get lower confidence, but still some autonomy
    let confidenceMultiplier = 1.0;
    if (total < 5) {
      // Scale from 20% confidence at 1 interaction to 80% confidence at 4 interactions
      confidenceMultiplier = 0.2 + (0.6 * (total - 1) / 4);
    }
    
    autonomy *= confidenceMultiplier;
    
    return Math.max(0, Math.min(100, Math.round(autonomy)));
  }

  /**
   * Get agent metrics for a specific client
   */
  async getAgentMetrics(agentId: string, clientId: string): Promise<AgentMetrics | null> {
    try {
      const metricsId = `${agentId}_${clientId}`;
      const metricsRef = doc(db, this.METRICS_COLLECTION, metricsId);
      const metricsSnap = await getDoc(metricsRef);
      
      if (metricsSnap.exists()) {
        return metricsSnap.data() as AgentMetrics;
      }
      return null;
    } catch (error) {
      console.error('Error fetching agent metrics:', error);
      return null;
    }
  }

  /**
   * Get all of the current user's metrics for an agent across their clients.
   * The userId filter is required by per-tenant Firestore rules (unscoped
   * collection scans are denied).
   */
  async getAllAgentMetrics(agentId: string, userId: string): Promise<AgentMetrics[]> {
    try {
      const metricsQuery = query(
        collection(db, this.METRICS_COLLECTION),
        where('agentId', '==', agentId),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(metricsQuery);
      
      const metrics: AgentMetrics[] = [];
      querySnapshot.forEach((doc) => {
        metrics.push(doc.data() as AgentMetrics);
      });
      
      return metrics;
    } catch (error) {
      console.error('Error fetching all agent metrics:', error);
      return [];
    }
  }

  /**
   * Initialize default agents in Firebase if they don't exist
   */
  async initializeDefaultAgents(defaultAgents: Partial<AIAgent>[]): Promise<void> {
    try {
      for (const agentConfig of defaultAgents) {
        if (agentConfig.id) {
          const existingAgent = await this.getAgentConfig(agentConfig.id);
          if (!existingAgent) {
            const fullAgent: AIAgent = {
              ...agentConfig,
              clientAdaptations: {},
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            } as AIAgent;
            
            await this.saveAgentConfig(fullAgent);
          }
        }
      }
    } catch (error) {
      console.error('Error initializing default agents:', error);
    }
  }

  /**
   * Reset autonomy for a specific agent and client
   */
  async resetAgentAutonomy(agentId: string, clientId: string): Promise<void> {
    try {
      const metricsId = `${agentId}_${clientId}`;
      
      // Reset metrics to zero state
      const resetMetrics = {
        agentId,
        clientId,
        totalInteractions: 0,
        positiveInteractions: 0,
        negativeInteractions: 0,
        modifiedInteractions: 0,
        averageTokensUsed: 0,
        averageResponseTime: 0,
        autonomyLevel: 0,
        lastCalculated: new Date().toISOString()
      };
      
      await setDoc(doc(db, this.METRICS_COLLECTION, metricsId), resetMetrics);
      console.log(`Reset autonomy for agent ${agentId} with client ${clientId}`);
    } catch (error) {
      console.error('Error resetting agent autonomy:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const agentConfigService = new AgentConfigurationService();
